import { useTranslation } from 'react-i18next'
import { CalendarClock, Download, FileSpreadsheet } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ReportStatusPill } from './ReportStatusPill'
import type { ReportExecution, ReportTarget } from '@/services/reports/reportsApi'
import { formatDateTime } from '../../utils/format'

export interface ReportsSheetProps {
  target: ReportTarget
  /** Reports already loaded by the card — the sheet adds no request of its own. */
  executions: ReportExecution[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Every report produced for one object, with both formats to download. The
 * card behind it lists only the last few; this is where someone goes looking
 * for the one from three weeks ago.
 */
export function ReportsSheet({ target, executions, open, onOpenChange }: ReportsSheetProps) {
  const { t } = useTranslation()

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full gap-0 sm:max-w-2xl">
        <SheetHeader className="border-b">
          <SheetTitle>{t('reports.title')}</SheetTitle>
          <SheetDescription>{target.name ?? t('reports.allReports')}</SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('reports.name')}</TableHead>
                <TableHead>{t('reports.statusLabel')}</TableHead>
                <TableHead>{t('reports.created')}</TableHead>
                <TableHead className="text-right">{t('reports.download')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {executions.map((execution) => (
                <TableRow key={execution.id}>
                  <TableCell className="text-xs">
                    <div className="flex items-center gap-1.5 font-medium">
                      {execution.scheduleId && (
                        <CalendarClock className="size-3.5 shrink-0 text-muted-foreground" />
                      )}
                      {execution.name || execution.templateName}
                    </div>
                    <div className="text-muted-foreground">{execution.templateName}</div>
                    {execution.messages.length > 0 && (
                      <div className="text-(--ad-badge-red)">{execution.messages.join(' · ')}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <ReportStatusPill status={execution.status} />
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
                    {formatDateTime(execution.createTime)}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {execution.result && (
                      <Button type="button" variant="ghost" size="icon" className="size-7" asChild>
                        <a
                          href={execution.result}
                          target="_blank"
                          rel="noreferrer"
                          title={t('reports.open')}
                        >
                          <Download className="size-4" />
                        </a>
                      </Button>
                    )}
                    {execution.secondResult && (
                      <Button type="button" variant="ghost" size="icon" className="size-7" asChild>
                        <a
                          href={execution.secondResult}
                          target="_blank"
                          rel="noreferrer"
                          title={t('reports.openCsv')}
                        >
                          <FileSpreadsheet className="size-4" />
                        </a>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {executions.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground">{t('reports.noReports')}</p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
